import {
  faBusinessTime,
  faCircleCheck,
  faForward,
  faLink,
  faListCheck,
  faLock,
  faLockOpen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import BadgeRow from 'src/components/ui/BadgeRow';
import Button from 'src/components/ui/Button';
import ButtonRow from 'src/components/ui/ButtonRow';
import SectionLabel from 'src/components/ui/SectionLabel';
import SelectableCard from 'src/components/ui/SelectableCard';
import StatusBadge from 'src/components/ui/StatusBadge';
import { MAINTENANCE_SCOPE } from 'src/constants/maintenance';
import { useDateLocale } from 'src/hooks/useDateLocale';
import { isPastScheduledFinish } from 'src/utils/maintenance';

import AnswersPreviewCard from './AnswersPreviewCard';
import { DowntimeViewCard } from './DowntimeCards';

const CompleteEvent = ({
  eventName,
  summary,
  linkedDowntime,
  isFinishing,
  onViewFile,
  onBack,
  onComplete,
  onCancel,
}) => {
  const { formattedDateTime, formattedTime } = useDateLocale();
  const done = summary.filter(item => !item.skipped).length;
  const skipped = summary.filter(item => item.skipped).length;

  const isLate = linkedDowntime && isPastScheduledFinish(linkedDowntime.finish);
  const [freeNow, setFreeNow] = useState(true);
  const effectiveFreeNow = isLate ? true : freeNow;

  const scheduledFinish = linkedDowntime ? formattedDateTime(linkedDowntime.finish) : '';

  const completeLabel = linkedDowntime
    ? effectiveFreeNow
      ? 'Complete & free machine'
      : 'Complete & keep blocked'
    : 'Complete event';

  return (
    <div>
      <div className='mnt-run-title'>{eventName}</div>
      <BadgeRow center>
        <StatusBadge dot size='sm' uppercase={false} variant='warning'>
          In progress
        </StatusBadge>
        {linkedDowntime && (
          <StatusBadge size='sm' uppercase={false} variant='warning' icon={faLink}>
            Downtime linked
          </StatusBadge>
        )}
      </BadgeRow>

      <div className='mnt-complete-hero'>
        <div className='mnt-complete-hero__title'>All requirements handled</div>
        <div className='mnt-complete-hero__stats'>
          <div>
            <div className='mnt-complete-hero__num mnt-complete-hero__num--done'>{done}</div>
            <div className='mnt-complete-hero__cap'>completed</div>
          </div>
          <div>
            <div className='mnt-complete-hero__num mnt-complete-hero__num--skipped'>{skipped}</div>
            <div className='mnt-complete-hero__cap'>skipped</div>
          </div>
        </div>
      </div>

      <SectionLabel icon={faListCheck} hint='tap to review answers'>
        Summary
      </SectionLabel>
      {summary.map(item => (
        <AnswersPreviewCard
          key={item.fulfillment.uri}
          requirement={item.requirement}
          isInstance={item.requirement.scope === MAINTENANCE_SCOPE.INSTANCE}
          instructions={item.instructions}
          values={item.values}
          files={item.files}
          skipped={item.skipped}
          onViewFile={onViewFile}
        />
      ))}

      {linkedDowntime && (
        <>
          <SectionLabel icon={faBusinessTime}>Linked downtime</SectionLabel>

          {isLate ? (
            <p className='mnt-section-help'>
              This ran past the planned window, so the machine will be freed now.
            </p>
          ) : (
            <>
              <SelectableCard
                id='free'
                name='mnt-ready'
                icon={faLockOpen}
                title='Free the machine now'
                desc='Mark the downtime done and release the machine for production right away.'
                active={freeNow}
                onSelect={() => setFreeNow(true)}
              />
              <SelectableCard
                id='keep'
                name='mnt-ready'
                icon={faLock}
                title='Keep it blocked'
                desc={`Leave the downtime in place; the machine stays unavailable until ${scheduledFinish}.`}
                active={!freeNow}
                onSelect={() => setFreeNow(false)}
              />
            </>
          )}

          <DowntimeViewCard
            downtime={linkedDowntime}
            finishOverride={effectiveFreeNow ? `Closes now, ${formattedTime(new Date())}` : null}
            chip={
              effectiveFreeNow ? (
                <StatusBadge size='sm' tone='outline' uppercase={false} variant='warning'>
                  Will close now
                </StatusBadge>
              ) : (
                <StatusBadge size='sm' tone='outline' uppercase={false} variant='warning'>
                  Stays blocked
                </StatusBadge>
              )
            }
          />
          <p className='mnt-section-help'>
            {effectiveFreeNow
              ? 'Completing closes the downtime now so the machine is released early.'
              : `The machine stays blocked until ${scheduledFinish}.`}
          </p>
        </>
      )}

      <Button
        fill
        block
        variant='success'
        className='mt-3 mb-2'
        disabled={isFinishing}
        loading={isFinishing}
        icon={<FontAwesomeIcon icon={faCircleCheck} />}
        onClick={() => onComplete(effectiveFreeNow)}
      >
        {isFinishing ? 'Completing…' : completeLabel}
      </Button>
      <ButtonRow>
        <Button block variant='default' disabled={isFinishing} onClick={onBack}>
          Back
        </Button>
        <Button
          block
          variant='danger'
          disabled={isFinishing}
          icon={<FontAwesomeIcon icon={faForward} />}
          onClick={() => onCancel(effectiveFreeNow)}
        >
          Cancel event
        </Button>
      </ButtonRow>
    </div>
  );
};

CompleteEvent.propTypes = {
  eventName: PropTypes.string,
  summary: PropTypes.array.isRequired,
  linkedDowntime: PropTypes.object,
  isFinishing: PropTypes.bool,
  onViewFile: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

CompleteEvent.defaultProps = {
  eventName: 'Maintenance event',
  linkedDowntime: null,
  isFinishing: false,
  onViewFile: null,
};

export default CompleteEvent;
